/**
 * Auth store (Zustand).
 *
 * IMPORTANT: master_key și private_key sunt persistate în `sessionStorage`
 * (tab-scoped), NICIODATĂ în `localStorage`. Supraviețuiesc refresh-ului,
 * dar se pierd la închiderea tab-ului.
 *
 * Vezi `stores/sessionPersist.ts` pentru detalii și tradeoff-uri.
 */

import { create } from 'zustand';
import libsodium from 'libsodium-wrappers-sumo';

import type { KeyPair } from '@/crypto/sodium';
import { base64ToBytes, deriveMasterKey, kdfParamsFromServer } from '@/crypto/sodium';
import { authApi } from '@/api/auth';
import { runSessionCleanup } from '@/stores/sessionCleanup';
import type { TenantMembership } from '@/api/auth';
import {
  clearSession as clearPersistedSession,
  loadSession,
  saveSession,
  updateCurrentTenant,
} from '@/stores/sessionPersist';

interface User {
  id: string;
  email: string;
  tenants: TenantMembership[];
}

interface AuthState {
  user: User | null;
  masterKey: Uint8Array | null;
  keyPair: KeyPair | null;
  currentTenantId: string | null;
  expiresAt: Date | null;

  setSession: (
    user: User,
    masterKey: Uint8Array,
    keyPair: KeyPair,
    expiresAt?: string | null,
  ) => void;
  setTenant: (tenantId: string) => void;
  /**
   * Adaugă un tenant nou la lista user-ului (după POST /tenants) fără să
   * resetăm currentTenantId. Opțional: switch pe el imediat.
   */
  addTenant: (tenant: TenantMembership, switchTo?: boolean) => void;
  /**
   * Înlocuiește lista de tenanți cu cea de pe server (`/auth/me`). Spațiul
   * curent rămâne dacă mai există; altfel trecem pe `preferTenantId` sau pe
   * primul din listă.
   */
  syncTenants: (tenants: TenantMembership[], preferTenantId?: string) => void;
  /** Scoate un tenant din listă (după ce userul l-a părăsit). */
  removeTenant: (tenantId: string) => void;
  /**
   * Înlocuiește masterKey-ul curent (după schimbare parolă master).
   * keyPair rămâne același — doar blob-ul criptat pe server se rescrie.
   */
  rotateMasterKey: (nextMasterKey: Uint8Array) => void;
  clearSession: () => void;
  logout: () => Promise<void>;
  /**
   * Actualizează `expiresAt` local după un heartbeat/refresh server-side.
   * Folosit de SessionTimer ca să sincronizeze counter-ul vizibil cu
   * rolling session-ul din backend.
   */
  extendSession: (expiresAt: string) => void;
  /**
   * Re-derivă master_key din parola dată și o compară (constant-time) cu
   * cea din memorie. Folosit pentru confirmarea acțiunilor destructive.
   * Nu trimite parola la server.
   */
  verifyPassword: (password: string) => Promise<boolean>;
  isAuthenticated: () => boolean;
  /** Hidratează state-ul din sessionStorage la boot (înainte de render). */
  hydrate: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  masterKey: null,
  keyPair: null,
  currentTenantId: null,
  expiresAt: null,

  setSession: (user, masterKey, keyPair, expiresAt = null) => {
    const tenantId = user.tenants[0]?.tenant_id ?? null;
    set({
      user,
      masterKey,
      keyPair,
      currentTenantId: tenantId,
      expiresAt: expiresAt ? new Date(expiresAt) : null,
    });
    saveSession({
      user,
      currentTenantId: tenantId,
      masterKey,
      keyPair,
      expiresAt,
    });
  },

  rotateMasterKey: (nextMasterKey: Uint8Array) => {
    const state = get();
    if (!state.user || !state.keyPair) return;
    const oldKey = state.masterKey;
    set({ masterKey: nextMasterKey });
    saveSession({
      user: state.user,
      currentTenantId: state.currentTenantId,
      masterKey: nextMasterKey,
      keyPair: state.keyPair,
      expiresAt: state.expiresAt ? state.expiresAt.toISOString() : null,
    });
    if (oldKey) oldKey.fill(0);
  },

  setTenant: (tenantId) => {
    set({ currentTenantId: tenantId });
    updateCurrentTenant(tenantId);
  },

  syncTenants: (tenants, preferTenantId) => {
    const { user, masterKey, keyPair, expiresAt, currentTenantId } = get();
    if (!user || !masterKey || !keyPair) return;
    const has = (id: string | null | undefined) =>
      !!id && tenants.some((t) => t.tenant_id === id);
    const nextTenantId = has(preferTenantId)
      ? preferTenantId!
      : has(currentTenantId)
        ? currentTenantId
        : (tenants[0]?.tenant_id ?? null);
    const unchanged =
      nextTenantId === currentTenantId &&
      JSON.stringify(tenants) === JSON.stringify(user.tenants);
    if (unchanged) return;
    const newUser: User = { ...user, tenants };
    set({ user: newUser, currentTenantId: nextTenantId });
    saveSession({
      user: newUser,
      currentTenantId: nextTenantId,
      masterKey,
      keyPair,
      expiresAt: expiresAt ? expiresAt.toISOString() : null,
    });
  },

  removeTenant: (tenantId) => {
    const { user, masterKey, keyPair, expiresAt, currentTenantId } = get();
    if (!user || !masterKey || !keyPair) return;
    const tenants = user.tenants.filter((t) => t.tenant_id !== tenantId);
    const newUser: User = { ...user, tenants };
    const nextTenantId =
      currentTenantId === tenantId ? (tenants[0]?.tenant_id ?? null) : currentTenantId;
    set({ user: newUser, currentTenantId: nextTenantId });
    saveSession({
      user: newUser,
      currentTenantId: nextTenantId,
      masterKey,
      keyPair,
      expiresAt: expiresAt ? expiresAt.toISOString() : null,
    });
  },

  addTenant: (tenant, switchTo = false) => {
    const { user, masterKey, keyPair, expiresAt, currentTenantId } = get();
    if (!user || !masterKey || !keyPair) return;
    // Evită duplicate
    if (user.tenants.some((t) => t.tenant_id === tenant.tenant_id)) return;
    const newUser: User = {
      ...user,
      tenants: [...user.tenants, tenant],
    };
    const nextTenantId = switchTo ? tenant.tenant_id : currentTenantId;
    set({ user: newUser, currentTenantId: nextTenantId });
    // Resync blob persistent — păstrăm expiresAt-ul existent.
    saveSession({
      user: newUser,
      currentTenantId: nextTenantId,
      masterKey,
      keyPair,
      expiresAt: expiresAt ? expiresAt.toISOString() : null,
    });
  },

  clearSession: () => {
    // Best-effort zeroize — JS nu garantează ștergerea, dar e un reflex sănătos
    const { masterKey, keyPair } = get();
    if (masterKey) masterKey.fill(0);
    if (keyPair?.privateKey) keyPair.privateKey.fill(0);
    set({
      user: null,
      masterKey: null,
      keyPair: null,
      currentTenantId: null,
      expiresAt: null,
    });
    clearPersistedSession();
    // Datele decriptate ținute în afara acestui store (cache TanStack Query,
    // blob URL-uri, drafturi vechi în clar) — vezi stores/sessionCleanup.ts.
    runSessionCleanup();
  },

  extendSession: (expiresAt: string) => {
    const state = get();
    if (!state.user || !state.masterKey || !state.keyPair) return;
    const next = new Date(expiresAt);
    set({ expiresAt: next });
    saveSession({
      user: state.user,
      currentTenantId: state.currentTenantId,
      masterKey: state.masterKey,
      keyPair: state.keyPair,
      expiresAt: next.toISOString(),
    });
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch {
      // ignorăm erori — oricum vrem să curățăm state-ul local
    }
    get().clearSession();
  },

  verifyPassword: async (password: string) => {
    const { user, masterKey } = get();
    if (!user || !masterKey) return false;
    const saltResp = await authApi.getOwnKdf();
    const derived = await deriveMasterKey(
      password,
      base64ToBytes(saltResp.kdf_salt),
      kdfParamsFromServer(saltResp.kdf_params),
    );
    const match = constantTimeEqual(derived, masterKey);
    libsodium.memzero(derived);
    return match;
  },

  isAuthenticated: () => get().user !== null && get().masterKey !== null,

  hydrate: () => {
    const loaded = loadSession();
    if (!loaded) return;
    set({
      user: loaded.user,
      masterKey: loaded.masterKey,
      keyPair: loaded.keyPair,
      currentTenantId: loaded.currentTenantId,
      expiresAt: loaded.expiresAt,
    });
  },
}));

function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}
